import axios from "axios";
import notify from "./toastify";

// Create axios instance
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || "http://localhost:3005",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { response } = error;
    
    if (response) {
      switch (response.status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          window.location.href = "/login";
          break;
        case 403:
          notify.error("Không có quyền truy cập");
          break;
        case 404:
          notify.error("Không tìm thấy tài nguyên");
          break;
        case 422:
          // Validation error
          const errors = response.data.errors;
          if (errors && Array.isArray(errors)) {
            errors.forEach((err) => {
              notify.error(`${err.field}: ${err.message}`);
            });
          } else {
            notify.error(response.data.message || "Dữ liệu không hợp lệ");
          }
          break;
        case 500:
          notify.error("Lỗi server - vui lòng thử lại sau");
          break;
        default:
          notify.error(response.data.message || "Có lỗi xảy ra");
      }
    } else {
      // Network error
      notify.error("Không thể kết nối đến server");
    }
    
    return Promise.reject(error);
  }
);


// API endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    REGISTER: "/api/v1/auth/register",
    LOGIN: "/api/v1/auth/login",
    VALIDATE: "/api/v1/auth/validate",
  },
  
  // Devices
  DEVICES: {
    LIST: "/api/v1/devices",
    DETAIL: (id) => `/api/v1/devices/${id}`,
    CONTROL: (id) => `/api/v1/devices/${id}/control`,
    ACTIONS: (id) => `/api/v1/devices/${id}/actions`,
    EMERGENCY_STOP: "/api/v1/devices/emergency-stop",
    STATISTICS: "/api/v1/devices/statistics/overview",
  },
  
  // Sensors
  SENSORS: {
    LATEST: "/api/v1/sensors/latest",
    BY_DEVICE: (deviceId) => `/api/v1/sensors/device/${deviceId}`,
    ADD: "/api/v1/sensors",
  },
  
  // AI
  AI: {
    ANALYZE: "/api/v1/ai/analyze",
    STATUS: "/api/v1/ai/status",
  },
  
  // Camera
  CAMERA: {
    STATUS: "/api/v1/camera/status",
    CONTROL: "/api/v1/camera/control",
    CAPTURE: "/api/v1/camera/capture",
  },
  
  // Users
  USERS: {
    PROFILE: "/api/v1/users/profile",
    UPDATE_PROFILE: "/api/v1/users/profile",
    LIST: "/api/v1/users",
  },
  
  // System
  SYSTEM: {
    STATUS: "/api/v1/system/status",
    LOGS: "/api/v1/system/logs",
    HEALTH: "/health",
  },
  
  // Legacy endpoints (for backward compatibility)
  LEGACY: {
    NODE_CONTROL: "/node_control",
    NODE_DATA: "/node_data",
  },
};

// Helper functions
export const apiCall = async (method, url, data = null, config = {}) => {
  try {
    const response = await axiosInstance({
      method,
      url,
      data,
      ...config,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Device control helper
export const controlDevice = async (deviceId, action, value = null) => {
  return apiCall("PUT", API_ENDPOINTS.DEVICES.CONTROL(deviceId), {
    action,
    value,
  });
};

// Sensor data helper
export const addSensorData = async (deviceId, temperature, humidity, pressure = null) => {
  return apiCall("POST", API_ENDPOINTS.SENSORS.ADD, {
    deviceId,
    temperature,
    humidity,
    pressure,
  });
};

// AI analysis helper
export const triggerAiAnalysis = async (sensorData, analysisType = "environmental") => {
  return apiCall("POST", API_ENDPOINTS.AI.ANALYZE, {
    sensor_data: sensorData,
    analysis_type: analysisType,
  });
};

// Camera control helper
export const controlCamera = async (action) => {
  return apiCall("POST", API_ENDPOINTS.CAMERA.CONTROL, { action });
};

// Emergency stop helper
export const emergencyStop = async () => {
  return apiCall("POST", API_ENDPOINTS.DEVICES.EMERGENCY_STOP);
};

export default axiosInstance;
